import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV, CONTACT } from "@/data/site";
import { Logo } from "@/components/common/Logo";
import { lockScroll } from "./SmoothScroll";

const linkCls = ({ isActive }) =>
  `relative py-2 font-display text-[15px] font-semibold uppercase tracking-[0.12em] transition-colors duration-300 ${isActive ? "text-brand" : "text-ink/85 hover:text-ink"}`;

const MobileMenu = ({ onClose }) => (
  <motion.div
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}
    className="fixed inset-0 z-[70] flex flex-col bg-canvas cad-grid lg:hidden" data-testid="mobile-menu"
  >
    <div className="flex h-20 items-center justify-between border-b border-canvas-line px-5">
      <Logo testid="mobile-menu-logo" />
      <button onClick={onClose} className="flex h-11 w-11 items-center justify-center border border-canvas-line text-ink" data-testid="mobile-menu-close" aria-label="Close menu">
        <X className="h-5 w-5" />
      </button>
    </div>
    <nav className="flex flex-1 flex-col justify-center gap-2 px-5">
      {NAV.map((n, i) => (
        <motion.div key={n.to} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 + i * 0.06 }}>
          <NavLink to={n.to} onClick={onClose} data-testid={`mobile-nav-${n.label.toLowerCase()}`}
            className={({ isActive }) => `flex items-baseline gap-4 py-2 font-display text-5xl font-bold uppercase ${isActive ? "text-brand" : "text-ink"}`}>
            <span className="label-mono text-steel">0{i + 1}</span>{n.label}
          </NavLink>
        </motion.div>
      ))}
    </nav>
    <div className="border-t border-canvas-line px-5 py-6 text-sm text-steel">
      <a href={`mailto:${CONTACT.enquiriesEmail}`} className="block text-ink">{CONTACT.enquiriesEmail}</a>
      <a href={`tel:${CONTACT.phones[0].tel}`} className="mt-1 block">Dublin {CONTACT.phones[0].value}</a>
    </div>
  </motion.div>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    lockScroll(open);
    return () => {
      lockScroll(false);
    };
  }, [open]);

  return (
    <>
      <header className="fixed inset-x-0 top-7 z-50 border-b border-canvas-line bg-canvas/90 backdrop-blur-xl" data-testid="site-header">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
          <Logo />
          <nav className="hidden items-center gap-8 lg:flex">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} className={linkCls} data-testid={`nav-${n.label.toLowerCase()}`}>{n.label}</NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/contact" data-testid="nav-enquiry-btn"
              className="hidden h-11 items-center bg-brand px-5 font-display text-sm font-semibold uppercase tracking-[0.1em] text-canvas transition-colors duration-300 hover:bg-leaf sm:inline-flex">
              Send an enquiry
            </Link>
            <button onClick={() => setOpen(true)} className="flex h-11 w-11 items-center justify-center border border-canvas-line text-ink lg:hidden" data-testid="mobile-menu-open" aria-label="Open menu">
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>{open && <MobileMenu onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
};
